import { Card, Divider, Flex, Tag, theme, Tooltip } from 'antd';

import verifiedIcon from '@assets/biodrone_verified.svg';
import ColorDot from '@components/modal/objectPickerModal/ColorDot';
import BaseText from '@components/typography/BaseText';
import { PROJECT_TYPE_LABELS } from '@utils/constants';
import { isSystemNode } from '@utils/taxonomyTree';

// A grouping row (no model for this project type) cannot be annotated, so it sits back.
const GROUPING_OPACITY = 0.6;

/**
 * One object in the annotation panel's tree. The panel sits beside the map, so
 * the row is compact: no thumbnail, no description. The colour dot is the
 * stroke the boxes drawn for this object get.
 *
 * A node with no model for the project type is a grouping row: it reads muted,
 * and the panel refuses to make it the active object.
 *
 * @param {object} props.object - a taxonomy node, as `useObjects` hands it out
 * @param {string} props.mode - a `PROJECT_MODE` value, named in the no-model tooltip
 * @param {boolean} [props.selected] - the object boxes are drawn for
 * @param {number} [props.count] - boxes drawn for this object in this session;
 *   left out until the session has any
 * @param {import('react').ReactNode} [props.children] - a block under the row line,
 *   inside the card and set off by a guide line (the active object's labels)
 */
const AnnotationObjectCard = ({ object, mode, selected = false, count, children }) => {
	const { token } = theme.useToken();

	const isSystem = isSystemNode(object);
	const isGrouping = !object.is_available;
	const projectType = PROJECT_TYPE_LABELS[mode] ?? 'this project type';

	return (
		<Card
			size="small"
			style={{
				borderColor: selected ? token.colorPrimary : undefined,
				opacity: isGrouping ? GROUPING_OPACITY : 1,
			}}>
			<Flex vertical gap="small">
				<Flex align="center" gap="small">
					<ColorDot color={object.color} shape="round" aria-hidden />

					<BaseText
						size="sm"
						strong={selected}
						ellipsis
						type={isGrouping ? 'secondary' : undefined}
						style={{ flex: 1, minWidth: 0 }}>
						{object.name}
					</BaseText>

					{isSystem && (
						<Tooltip title="Biodrone Verified">
							<img
								src={verifiedIcon}
								alt="Biodrone Verified"
								width={token.sizeMS}
								height={token.sizeMS}
								style={{ flex: 'none' }}
							/>
						</Tooltip>
					)}

					{isGrouping && (
						<Tooltip title={`No model is bound for ${projectType} yet`}>
							<Tag variant="outlined" style={{ flex: 'none' }}>
								No model
							</Tag>
						</Tooltip>
					)}

					{count !== undefined && (
						<BaseText
							size="sm"
							type={selected ? 'success' : 'secondary'}
							aria-label={`${count} annotations`}
							style={{ flex: 'none' }}>
							{count}
						</BaseText>
					)}
				</Flex>

				{children && (
					<Flex gap="small" align="stretch">
						<Divider type="vertical" style={{ height: 'auto', alignSelf: 'stretch' }} />
						<Flex vertical flex={1} style={{ minWidth: 0 }}>
							{children}
						</Flex>
					</Flex>
				)}
			</Flex>
		</Card>
	);
};

export default AnnotationObjectCard;
